'use client';

import { useEffect, useState } from 'react';

import { CardDefinition, Wording } from '@/types';
import Filters from '@/components/Filters';
import Cards from '@/components/Cards';
import Button from '@/components/Button';

import { useConfig } from '@/app/contexts/ConfigContext';
import { getMediaData } from '@/services/server/media';
import { TagProps } from '@/types';
import { FITNESS_WORDING_EN } from '@/wording/fitness/FITNESS_WORDING_EN';
import { SERVICE_TYPES } from '@/helpers/enums';
import { MEDITATION_WORDING_EN } from '@/wording/meditation/MEDITATION_WORDING_EN';
import { ASTROLOGY_WORDING_EN } from '@/wording/astrology/ASTROLOGY_WORDING_EN';
import { LOVE_WORDING_EN } from '@/wording/love/LOVE_WORDING_EN';

const ITEMS_PER_PAGE = 9;
const DEFAULT_MEDIA_FILTER: keyof Wording = 'page_media_content_menu_all';

export default function MediaContent({ tag }: TagProps) {
  const { wording, config, selectedLanguage } = useConfig();

  const [items, setItems] = useState<CardDefinition[]>([]);
  const [maxItems, setMaxItems] = useState(ITEMS_PER_PAGE);
  const [selectedState, setSelectedState] =
    useState<keyof Wording>(DEFAULT_MEDIA_FILTER);

  let selectedLabel: keyof Wording;
  let filterArray: { name: string; value: keyof Wording }[] = [];

  switch (config.serviceType) {
    case SERVICE_TYPES.YOGA:
    case SERVICE_TYPES.PILATES:
    case SERVICE_TYPES.COOKING:
      selectedLabel = FITNESS_WORDING_EN[selectedState] as keyof Wording;
      break;

    case SERVICE_TYPES.FITNESS:
      selectedLabel = FITNESS_WORDING_EN[selectedState] as keyof Wording;
      filterArray = [
        {
          name: wording!.page_media_content_menu_all as string,
          value: 'page_media_content_menu_all',
        },
        {
          name: wording!.page_media_content_menu_biceps as string,
          value: 'page_media_content_menu_biceps',
        },
        {
          name: wording!.page_media_content_menu_triceps as string,
          value: 'page_media_content_menu_triceps',
        },
        {
          name: wording!.page_media_content_menu_back as string,
          value: 'page_media_content_menu_back',
        },
        {
          name: wording!.page_media_content_menu_chest as string,
          value: 'page_media_content_menu_chest',
        },
        {
          name: wording!.page_media_content_menu_legs as string,
          value: 'page_media_content_menu_legs',
        },
        {
          name: wording!.page_media_content_menu_shoulders as string,
          value: 'page_media_content_menu_shoulders',
        },
        {
          name: wording!.page_media_content_menu_abdominals as string,
          value: 'page_media_content_menu_abdominals',
        },
      ];
      break;
    case SERVICE_TYPES.MEDITATION:
      selectedLabel = MEDITATION_WORDING_EN[selectedState] as keyof Wording;
      filterArray = [
        {
          name: wording!.page_media_content_menu_all as string,
          value: 'page_media_content_menu_all',
        },

        {
          name: wording!.page_media_content_menu_antistress as string,
          value: 'page_media_content_menu_antistress',
        },
        {
          name: wording!.page_media_content_menu_mindfulness as string,
          value: 'page_media_content_menu_mindfulness',
        },
        {
          name: wording!.page_media_content_menu_energyboost as string,
          value: 'page_media_content_menu_energyboost',
        },
        {
          name: wording!.page_media_content_menu_mantra as string,
          value: 'page_media_content_menu_mantra',
        },
      ];
      break;
    case SERVICE_TYPES.ASTROLOGY:
      selectedLabel = ASTROLOGY_WORDING_EN[selectedState] as keyof Wording;
      filterArray = [
        {
          name: wording!.page_media_content_menu_all as string,
          value: 'page_media_content_menu_all',
        },
        {
          name: wording!.page_media_content_menu_foundations as string,
          value: 'page_media_content_menu_foundations',
        },
        {
          name: wording!.page_media_content_menu_spirituality as string,
          value: 'page_media_content_menu_spirituality',
        },
        {
          name: wording!.page_media_content_menu_tarot as string,
          value: 'page_media_content_menu_tarot',
        },
        {
          name: wording!.page_media_content_menu_psychology as string,
          value: 'page_media_content_menu_psychology',
        },
        {
          name: wording!.page_media_content_menu_trends as string,
          value: 'page_media_content_menu_trends',
        },
      ];
      break;
    case SERVICE_TYPES.LOVE:
      selectedLabel = LOVE_WORDING_EN[selectedState] as keyof Wording;
      filterArray = [
        {
          name: wording!.page_media_content_menu_all as string,
          value: 'page_media_content_menu_all',
        },
        {
          name: wording!.page_media_content_menu_compatibility as string,
          value: 'page_media_content_menu_compatibility',
        },
        {
          name: wording!.page_media_content_menu_love as string,
          value: 'page_media_content_menu_love',
        },
      ];
      break;
    default:
      break;
  }

  const filteredItems = items.filter((item) => {
    // Tags/Labels use English wording as a base for consistency across languages.
    // They are translated at runtime using the selected language’s wording file in the Tag component.
    const allLabel = FITNESS_WORDING_EN.page_media_content_menu_all;

    return (
      selectedLabel === allLabel || item.tags.includes(selectedLabel ?? '')
    );
  });

  const itemsToDisplay = filteredItems.slice(0, maxItems);

  useEffect(() => {
    // Reset the active media filter only when the page changes service context.
    // This avoids leaking meditation-only filters such as "antistress" into
    // astrology/love while keeping "load more" usable on the current page.
    setSelectedState(DEFAULT_MEDIA_FILTER);
    setMaxItems(ITEMS_PER_PAGE);
  }, [config.serviceType, tag]);

  useEffect(() => {
    const fetchData = () => {
      try {
        const items = getMediaData(
          config.serviceType,
          selectedLanguage,
          config.contents.media,
          tag,
        );
        setItems(items);
      } catch (error) {
        console.error('[MediaContent] Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [config.serviceType, tag, selectedLanguage, config.contents.media]);

  return (
    <section className='sm:max-w-[1400px] max-w-[390px] mx-auto mt-8 px-4'>
      <div className='flex flex-row items-end sm:justify-center sm:gap-12 gap-6 overflow-auto'>
        {tag && wording && (
          <Filters
            options={filterArray}
            activeOption={selectedState}
            onSelect={(value) => {
              setSelectedState(value);
              setMaxItems(ITEMS_PER_PAGE);
            }}
          />
        )}
      </div>
      <Cards items={itemsToDisplay} />
      <div className='text-center sm:my-20 my-5'>
        {filteredItems.length > maxItems && (
          <Button
            variant='secondary'
            onClick={() => setMaxItems((prev) => prev + ITEMS_PER_PAGE)}
            themeBackgroundColor={config.theme.textColorDark}
            themeTextColor={config.theme.textColorLight}
          >
            {wording?.button_load_more}
          </Button>
        )}
      </div>
    </section>
  );
}
